<template>
  <div class="option-analysis">
    <header id="blog-header">
      <div class="inner">
        <h1 class="page-title">期权定价分析</h1>
        <span class="page-desc">基于 Black-Scholes 模型的期权定价与异常检测</span>
      </div>
    </header>

    <div class="option-content">
      <!-- 股票选择器 -->
      <div class="ticker-selector">
        <label for="ticker-select">选择股票：</label>
        <select id="ticker-select" v-model="selectedTicker" @change="loadAnalysis">
          <option value="">-- 请选择 --</option>
          <option v-for="t in tickers" :key="t.ticker" :value="t.ticker">
            {{ t.ticker }} (最新: {{ t.latest_date }})
          </option>
        </select>
        <select v-if="availableDates.length > 0" v-model="selectedDate" @change="loadAnalysisByDate" class="date-select">
          <option v-for="d in availableDates" :key="d" :value="d">{{ d }}</option>
        </select>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading">加载中...</div>

      <!-- 错误信息 -->
      <div v-if="error" class="error-msg">{{ error }}</div>

      <!-- 汇总面板 -->
      <div v-if="!selectedTicker && summaries.length > 0" class="summary-panel">
        <h2>全部股票概览</h2>
        <table class="summary-table">
          <thead>
            <tr>
              <th>股票</th>
              <th>分析日期</th>
              <th>期权总数</th>
              <th>看涨</th>
              <th>看跌</th>
              <th>异常数量</th>
              <th>平均隐含波动率</th>
              <th>总成交量</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in summaries" :key="s.ticker">
              <td class="ticker-cell">{{ s.ticker }}</td>
              <td>{{ s.date }}</td>
              <td>{{ s.total_options }}</td>
              <td>{{ s.call_count }}</td>
              <td>{{ s.put_count }}</td>
              <td :class="{ 'highlight-red': s.mispriced_count > 0 }">{{ s.mispriced_count }}</td>
              <td>{{ s.avg_implied_volatility != null ? (s.avg_implied_volatility * 100).toFixed(2) + '%' : '-' }}</td>
              <td>{{ formatNumber(s.total_volume) }}</td>
              <td><a href="#" @click.prevent="selectTicker(s.ticker)">查看详情</a></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 单个股票分析结果 -->
      <div v-if="analysisData && analysisData.length > 0" class="analysis-detail">
        <h2>{{ selectedTicker }} - {{ currentDate }} 分析结果</h2>

        <!-- 汇总指标 -->
        <div class="metrics-grid" v-if="summary">
          <div class="metric-card">
            <div class="metric-value">{{ summary.total_options }}</div>
            <div class="metric-label">期权总数</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">{{ summary.call_count }} / {{ summary.put_count }}</div>
            <div class="metric-label">看涨 / 看跌</div>
          </div>
          <div class="metric-card">
            <div class="metric-value" :class="{ 'highlight-red': summary.mispriced_count > 0 }">
              {{ summary.mispriced_count }}
            </div>
            <div class="metric-label">定价异常</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">
              {{ summary.avg_implied_volatility != null ? (summary.avg_implied_volatility * 100).toFixed(2) + '%' : '-' }}
            </div>
            <div class="metric-label">平均隐含波动率</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">{{ formatNumber(summary.total_volume) }}</div>
            <div class="metric-label">总成交量</div>
          </div>
        </div>

        <!-- 策略推荐 -->
        <div v-if="strategies.length > 0" class="strategy-panel">
          <h3>组合策略推荐
            <span class="strategy-meta" v-if="strategyMeta">
              ATM IV {{ formatPercent(strategyMeta.atm_iv) }} ·
              IV rank {{ strategyMeta.iv_rank != null ? strategyMeta.iv_rank : '数据积累中' }}
            </span>
          </h3>
          <div class="strategy-card" v-for="(s, si) in strategies" :key="si">
            <div class="strategy-title">
              <strong>{{ si + 1 }}. {{ s.strategy_type }}</strong>
              <span class="strategy-score">score {{ s.score }}</span>
            </div>
            <div class="strategy-legs">
              <span v-for="(l, li) in s.legs" :key="li" class="strategy-leg">
                {{ l.action === 'buy' ? '买' : '卖' }}
                {{ l.option_type === 'call' ? 'C' : 'P' }}
                {{ l.strike }} @ {{ formatPrice(l.price) }}
                <em>(模型 {{ formatPrice(l.model_price) }})</em>
              </span>
            </div>
            <div class="strategy-stats">
              {{ s.net_cost >= 0 ? '权利金支出' : '权利金收入' }} {{ formatPrice(Math.abs(s.net_cost)) }} ·
              最大盈利 {{ s.max_profit != null ? formatPrice(s.max_profit) : '不限' }} ·
              最大亏损 {{ formatPrice(s.max_loss) }} ·
              盈亏平衡 {{ s.breakeven.join(' / ') }}
            </div>
            <div class="strategy-rationale">{{ s.rationale }}</div>
          </div>
        </div>

        <!-- 类型筛选 -->
        <div class="filter-tabs">
          <button :class="{ active: filterType === 'all' }" @click="filterType = 'all'">全部</button>
          <button :class="{ active: filterType === 'call' }" @click="filterType = 'call'">看涨 (Call)</button>
          <button :class="{ active: filterType === 'put' }" @click="filterType = 'put'">看跌 (Put)</button>
          <button :class="{ active: filterType === 'mispriced' }" @click="filterType = 'mispriced'">仅异常</button>
        </div>

        <!-- 数据表格 -->
        <div class="table-wrapper">
          <table class="data-table">
            <thead>
              <tr>
                <th>类型</th>
                <th>执行价格</th>
                <th>到期日</th>
                <th>到期天数</th>
                <th>市场价格</th>
                <th>隐含波动率</th>
                <th>BSM价格</th>
                <th>模型均价</th>
                <th>偏差%</th>
                <th>异常</th>
                <th>偏差方向</th>
                <th>Delta</th>
                <th>Gamma</th>
                <th>成交量</th>
                <th>未平仓</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in filteredData" :key="idx"
                  :class="{ 'row-mispriced': row['定价异常'] === '是' }">
                <td>
                  <span :class="row['期权类型'] === '看涨' ? 'tag-call' : 'tag-put'">
                    {{ row['期权类型'] }}
                  </span>
                </td>
                <td>{{ formatPrice(row['执行价格']) }}</td>
                <td>{{ row['到期日'] }}</td>
                <td>{{ row['到期天数'] }}</td>
                <td>{{ formatPrice(row['市场价格']) }}</td>
                <td>{{ formatPercent(row['隐含波动率']) }}</td>
                <td>{{ formatPrice(row['BSM价格']) }}</td>
                <td>{{ formatPrice(row['模型均价']) }}</td>
                <td :class="deviationClass(row['偏差百分比'])">
                  {{ formatDeviation(row['偏差百分比']) }}
                </td>
                <td>
                  <span :class="row['定价异常'] === '是' ? 'badge-yes' : 'badge-no'">
                    {{ row['定价异常'] }}
                  </span>
                </td>
                <td class="direction-cell">{{ row['偏差方向'] || '-' }}</td>
                <td>{{ formatGreek(row['Delta']) }}</td>
                <td>{{ formatGreek(row['Gamma']) }}</td>
                <td>{{ row['成交量'] != null ? formatNumber(row['成交量']) : '-' }}</td>
                <td>{{ row['未平仓合约'] != null ? formatNumber(row['未平仓合约']) : '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'option-analysis',
  data: function () {
    return {
      tickers: [],
      summaries: [],
      selectedTicker: '',
      selectedDate: '',
      availableDates: [],
      analysisData: [],
      summary: null,
      currentDate: '',
      filterType: 'all',
      loading: false,
      error: '',
      // 策略推荐
      strategies: [],
      strategyMeta: null
    }
  },
  computed: {
    filteredData: function () {
      if (!this.analysisData) return []
      var self = this
      return this.analysisData.filter(function (row) {
        if (self.filterType === 'call') return row['期权类型'] === '看涨'
        if (self.filterType === 'put') return row['期权类型'] === '看跌'
        if (self.filterType === 'mispriced') return row['定价异常'] === '是'
        return true
      })
    }
  },
  mounted: function () {
    this.loadTickers()
    this.loadSummary()
  },
  methods: {
    loadTickers: function () {
      var self = this
      var reqUrl = this.$store.state.baseUrl + '/blog/api/options/tickers/'
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.tickers = res.tickers
        }
      }).catch(function (err) {
        console.error('加载股票列表失败:', err)
      })
    },

    loadSummary: function () {
      var self = this
      var reqUrl = this.$store.state.baseUrl + '/blog/api/options/summary/'
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.summaries = res.summaries
        }
      }).catch(function (err) {
        console.error('加载汇总数据失败:', err)
      })
    },

    selectTicker: function (ticker) {
      this.selectedTicker = ticker
      this.loadAnalysis()
    },

    loadAnalysis: function () {
      if (!this.selectedTicker) {
        this.analysisData = []
        this.summary = null
        this.currentDate = ''
        this.availableDates = []
        return
      }

      // 设置可用日期列表
      var self = this
      var tickerInfo = this.tickers.find(function (t) { return t.ticker === self.selectedTicker })
      if (tickerInfo) {
        this.availableDates = tickerInfo.available_dates
        this.selectedDate = tickerInfo.latest_date
      }

      this.fetchAnalysis(this.selectedTicker)
    },

    loadAnalysisByDate: function () {
      if (!this.selectedTicker || !this.selectedDate) return
      this.fetchAnalysis(this.selectedTicker, this.selectedDate)
    },

    loadStrategies: function (ticker) {
      var self = this
      self.strategies = []
      self.strategyMeta = null
      var reqUrl = this.$store.state.baseUrl + '/blog/api/options/strategies/' + ticker + '/'
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.strategies = res.strategies || []
          self.strategyMeta = { atm_iv: res.atm_iv, iv_rank: res.iv_rank }
        }
      }).catch(function (err) {
        console.error('加载策略推荐失败:', err)
      })
    },

    fetchAnalysis: function (ticker, date) {
      var self = this
      self.loading = true
      self.error = ''
      self.loadStrategies(ticker)

      var reqUrl = this.$store.state.baseUrl + '/blog/api/options/analysis/' + ticker + '/'
      if (date) {
        reqUrl = this.$store.state.baseUrl + '/blog/api/options/analysis/' + ticker + '/' + date + '/'
      }

      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.analysisData = res.data
          self.summary = res.summary
          self.currentDate = res.date
        } else {
          self.error = res.desc || '加载失败'
          self.analysisData = []
          self.summary = null
        }
        self.loading = false
      }).catch(function (err) {
        self.error = '请求失败: ' + err.statusText
        self.loading = false
      })
    },

    formatPrice: function (val) {
      if (val == null) return '-'
      return Number(val).toFixed(4)
    },

    formatPercent: function (val) {
      if (val == null) return '-'
      return (Number(val) * 100).toFixed(2) + '%'
    },

    formatDeviation: function (val) {
      if (val == null) return '-'
      return Number(val).toFixed(2) + '%'
    },

    formatGreek: function (val) {
      if (val == null) return '-'
      return Number(val).toFixed(4)
    },

    formatNumber: function (val) {
      if (val == null) return '-'
      return Number(val).toLocaleString()
    },

    deviationClass: function (val) {
      if (val == null) return ''
      var num = Number(val)
      if (num > 5) return 'deviation-high-pos'
      if (num < -5) return 'deviation-high-neg'
      if (num > 0) return 'deviation-pos'
      if (num < 0) return 'deviation-neg'
      return ''
    }
  }
}
</script>

<style scoped>
.option-analysis {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

#blog-header {
  text-align: center;
  padding: 2rem 0 1rem;
}

.page-title {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: #333;
}

.page-desc {
  color: #888;
  font-size: 1rem;
}

.option-content {
  padding: 1rem 0 4rem;
}

/* 选择器区域 */
.ticker-selector {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
}

.ticker-selector label {
  font-weight: 600;
  color: #333;
  white-space: nowrap;
}

.ticker-selector select {
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  background: #fff;
  cursor: pointer;
}

.date-select {
  margin-left: 0.5rem;
}

/* 加载与错误 */
.loading {
  text-align: center;
  padding: 2rem;
  color: #666;
  font-size: 1.1rem;
}

.error-msg {
  text-align: center;
  padding: 1rem;
  color: #e74c3c;
  background: #fdf0ef;
  border-radius: 4px;
  margin-bottom: 1rem;
}

/* 汇总面板 */
.summary-panel {
  margin-bottom: 2rem;
}

.summary-panel h2 {
  font-size: 1.4rem;
  color: #333;
  margin-bottom: 1rem;
  border-bottom: 2px solid #eee;
  padding-bottom: 0.5rem;
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.summary-table th {
  background: #f5f5f5;
  padding: 0.75rem 0.5rem;
  text-align: left;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #ddd;
}

.summary-table td {
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid #eee;
}

.summary-table tr:hover {
  background: #f8f9fa;
}

.ticker-cell {
  font-weight: 700;
  color: #2c3e50;
}

.summary-table a {
  color: #3498db;
  text-decoration: none;
}

.summary-table a:hover {
  text-decoration: underline;
}

/* 指标卡片 */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.metric-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.2rem;
  text-align: center;
  border: 1px solid #eee;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.3rem;
}

.metric-label {
  font-size: 0.85rem;
  color: #888;
}

/* 策略推荐 */
.strategy-panel {
  margin: 24px 0;
  padding: 16px;
  border: 1px solid #e8d9a0;
  border-radius: 6px;
  background: #fffbef;
}

.strategy-meta {
  font-size: 13px;
  font-weight: normal;
  color: #888;
  margin-left: 10px;
}

.strategy-card {
  padding: 10px 0;
  border-bottom: 1px dashed #e0d5b0;
}

.strategy-card:last-child {
  border-bottom: none;
}

.strategy-title {
  display: flex;
  justify-content: space-between;
}

.strategy-score {
  color: #b8860b;
  font-size: 13px;
}

.strategy-legs {
  margin: 6px 0;
}

.strategy-leg {
  display: inline-block;
  margin-right: 16px;
  font-family: monospace;
}

.strategy-leg em {
  color: #999;
  font-style: normal;
  font-size: 12px;
}

.strategy-stats {
  font-size: 13px;
  color: #555;
}

.strategy-rationale {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

/* 筛选标签 */
.filter-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.filter-tabs button {
  padding: 0.5rem 1.2rem;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #555;
  transition: all 0.2s;
}

.filter-tabs button:hover {
  background: #f0f0f0;
}

.filter-tabs button.active {
  background: #2c3e50;
  color: #fff;
  border-color: #2c3e50;
}

/* 数据表格 */
.table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  white-space: nowrap;
}

.data-table th {
  background: #f5f5f5;
  padding: 0.6rem 0.4rem;
  text-align: left;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #ddd;
  position: sticky;
  top: 0;
}

.data-table td {
  padding: 0.5rem 0.4rem;
  border-bottom: 1px solid #f0f0f0;
}

.data-table tr:hover {
  background: #fafafa;
}

.row-mispriced {
  background: #fff8e7 !important;
}

.row-mispriced:hover {
  background: #fff3d0 !important;
}

/* 标签样式 */
.tag-call {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 3px;
  font-size: 0.8rem;
  font-weight: 600;
  background: #e8f5e9;
  color: #2e7d32;
}

.tag-put {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 3px;
  font-size: 0.8rem;
  font-weight: 600;
  background: #fce4ec;
  color: #c62828;
}

.badge-yes {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 3px;
  font-size: 0.8rem;
  background: #e74c3c;
  color: #fff;
}

.badge-no {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 3px;
  font-size: 0.8rem;
  background: #eee;
  color: #999;
}

/* 偏差颜色 */
.deviation-pos { color: #e67e22; }
.deviation-neg { color: #3498db; }
.deviation-high-pos { color: #e74c3c; font-weight: 700; }
.deviation-high-neg { color: #2980b9; font-weight: 700; }

.highlight-red {
  color: #e74c3c;
  font-weight: 700;
}

.direction-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 分析详情标题 */
.analysis-detail h2 {
  font-size: 1.4rem;
  color: #333;
  margin-bottom: 1rem;
  border-bottom: 2px solid #eee;
  padding-bottom: 0.5rem;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .ticker-selector {
    flex-direction: column;
    align-items: stretch;
  }

  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .filter-tabs {
    flex-wrap: wrap;
  }

  .data-table {
    font-size: 0.75rem;
  }
}
</style>
